import { useCallback } from "react"

import { Handle, Position, useReactFlow } from "@xyflow/react"

import type { NodeProps } from "@xyflow/react"

import { Check, GitBranch, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { useNodeExecutionState } from "../../hooks/use-node-execution-state"
import { useWorkflowStore } from "../../stores/workflow-store"
import { NodeExecutionIndicator } from "../node-execution-indicator"
import { NodeOutputPopover } from "../node-output-popover"
import { NodeEditDialog } from "./node-edit-dialog"
import { ReferenceNodesPicker } from "./reference-nodes-picker"
import { TemplateField } from "./template-field"

export function ConditionalNode({ id, data, selected }: NodeProps) {
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData)
  const { deleteElements } = useReactFlow()
  const { isExecuting, isCompleted, output } = useNodeExecutionState(id)

  const handleModeChange = useCallback(
    (value: string) => {
      updateNodeData(id, { mode: value })
    },
    [id, updateNodeData]
  )

  const handleOperatorChange = useCallback(
    (value: string) => {
      updateNodeData(id, { operator: value })
    },
    [id, updateNodeData]
  )

  const handleShowOutputChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showOutput: checked })
    },
    [id, updateNodeData]
  )

  const handleShowInActivityHistoryChange = useCallback(
    (checked: boolean) => {
      updateNodeData(id, { showInActivityHistory: checked })
    },
    [id, updateNodeData]
  )

  const handleDelete = useCallback(() => {
    void deleteElements({ nodes: [{ id }] })
  }, [id, deleteElements])

  const mode = (data.mode as string) || "llm"
  const operator = (data.operator as string) || "contains"
  const hasOutput = isCompleted && !!output
  const renderEditor = (isModal = false) => (
    <div className="grid gap-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Mode</Label>
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger className="nodrag h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="llm">LLM-based (AI decides)</SelectItem>
            <SelectItem value="expression">Expression-based</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === "llm" ? (
        <div className="grid gap-1.5">
          <Label className="text-xs">Condition (for AI to evaluate)</Label>
          <TemplateField
            multiline
            className={cn("pr-8", isModal ? "min-h-[260px]" : "min-h-[50px]")}
            placeholder="Is the response positive?"
            value={(data.condition as string) || ""}
            onChange={(value) => updateNodeData(id, { condition: value })}
          />
        </div>
      ) : (
        <>
          <div className="grid gap-1.5">
            <Label className="text-xs">Operator</Label>
            <Select value={operator} onValueChange={handleOperatorChange}>
              <SelectTrigger className="nodrag h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contains">Contains</SelectItem>
                <SelectItem value="not_contains">Does not contain</SelectItem>
                <SelectItem value="equals">Equals</SelectItem>
                <SelectItem value="not_equals">Not equals</SelectItem>
                <SelectItem value="starts_with">Starts with</SelectItem>
                <SelectItem value="ends_with">Ends with</SelectItem>
                <SelectItem value="length_gt">Length greater than</SelectItem>
                <SelectItem value="length_lt">Length less than</SelectItem>
                <SelectItem value="regex">Regex match</SelectItem>
                <SelectItem value="is_empty">Is empty</SelectItem>
                <SelectItem value="is_not_empty">Is not empty</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!["is_empty", "is_not_empty"].includes(operator) && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Compare Value</Label>
              <TemplateField
                className="h-7 pr-8"
                placeholder="Value to compare..."
                value={(data.compareValue as string) || ""}
                onChange={(value) => updateNodeData(id, { compareValue: value })}
              />
            </div>
          )}
        </>
      )}
      <ReferenceNodesPicker
        currentNodeId={id}
        selectedNodeIds={(data.nodeReferences as string[] | undefined) ?? []}
        onChange={(ids) => updateNodeData(id, { nodeReferences: ids })}
      />
      <div className="grid gap-2 border-t pt-2">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={!!data.showOutput}
            className="nodrag"
            id={`${id}-showOutput${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowOutputChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showOutput${isModal ? "-modal" : ""}`}
          >
            Show Output
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={data.showInActivityHistory !== false}
            className="nodrag"
            id={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
            onCheckedChange={handleShowInActivityHistoryChange}
          />
          <Label
            className="cursor-pointer text-xs font-normal"
            htmlFor={`${id}-showInActivityHistory${isModal ? "-modal" : ""}`}
          >
            Show in Activity History
          </Label>
        </div>
      </div>
    </div>
  )

  return (
    <NodeOutputPopover disabled={!hasOutput} output={output || ""}>
      <Card
        className={cn(
          "relative min-w-[250px] border-2 shadow-md transition-[border-color,box-shadow] duration-200",
          data.active ? "border-success ring-success ring-2" : "border-border",
          selected && "ring-primary ring-offset-background ring-2 ring-offset-2",
          isExecuting && "workflow-node-executing",
          isCompleted && !isExecuting && "workflow-node-completed",
          hasOutput && "cursor-pointer"
        )}
      >
        <CardHeader className={cn("rounded-t-xl p-2", CATEGORY_TONES[3])}>
          <NodeExecutionIndicator
            hasOutput={hasOutput}
            isCompleted={isCompleted}
            isExecuting={isExecuting}
          />
          <CardTitle className="flex items-center justify-center gap-2 text-sm font-medium">
            <GitBranch className="size-4" />
            Conditional
            <NodeEditDialog
              description="Edit this conditional node in a larger workspace."
              title="Conditional settings"
            >
              {renderEditor(true)}
            </NodeEditDialog>
          </CardTitle>
          {selected && (
            <Button
              className="nodrag absolute -top-2.5 -right-2.5 size-6 rounded-full p-0 shadow-md"
              size="icon"
              variant="destructive"
              onClick={handleDelete}
            >
              <X className="size-3" />
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-3">
          {renderEditor()}
          <Handle id="input" position={Position.Left} type="target" />
          {/* True handle with green indicator */}
          <div
            className="absolute right-0 flex items-center gap-1"
            style={{ top: "40%", transform: "translateY(-50%) translateX(calc(100% + 4px))" }}
          >
            <div className="border-foreground bg-success flex size-4 items-center justify-center rounded-full border">
              <Check className="text-success-foreground size-2.5" />
            </div>
          </div>
          <Handle id="true" position={Position.Right} style={{ top: "40%" }} type="source" />
          {/* False handle with red indicator */}
          <div
            className="absolute right-0 flex items-center gap-1"
            style={{ top: "60%", transform: "translateY(-50%) translateX(calc(100% + 4px))" }}
          >
            <div className="border-foreground bg-destructive flex size-4 items-center justify-center rounded-full border">
              <X className="text-destructive-foreground size-2.5" />
            </div>
          </div>
          <Handle id="false" position={Position.Right} style={{ top: "60%" }} type="source" />
        </CardContent>
      </Card>
    </NodeOutputPopover>
  )
}
